import { InteractivityDesignDimensionsService } from './../../services/interactivity-design-dimensions.service';
import { Component, Input, ElementRef, ViewChild, ChangeDetectorRef, SimpleChanges, Output, EventEmitter, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { TimepointConfiguration } from './tracks.component';
import { ReduxService } from '../../../../../services/redux.service';
import { TimelineDataActions } from '../../redux/timeline-data-actions';
import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { InteractivityMediaService } from '../../services/interactivity-media.service';
import { Subscription } from 'rxjs';
import { IAnimationKeyFrame } from '../../redux/timeline-data.model';
import { bbGenerateShortId } from '../../../../../shared/utils/bbGenerateShortId.util';
import { BbDragEvent } from '../../../../../directives/bb-drag.directive';
import { InteractivityWidgetSelectionService } from '../../services/interactivity-widget-selection.service';
import { InteractivityWidgetAnimationService, KeyframeSelection } from '../../services/interactivity-widget-animation.service';

export interface Timepoint {
	key: string;
	val: string;
}

@Component({
	selector: 'timepoint-track',
	templateUrl: './timepoint-track.component.html',
	styleUrls: ['./timepoint-track.component.scss']
})

export class TimepointTrackComponent implements OnInit, OnChanges, OnDestroy {
	@ViewChild('timepointContainer') timepointContainer: ElementRef;

	@Input() timepointConfig: TimepointConfiguration;
	@Input() theme?: string;
	@Input() widgetId?: string;
	@Input() dragger: string;
	@Input() tooltip: 'left' | 'right' | 'hide';
	@Input() trackIndex: number;
	@Output() onDragging: EventEmitter<boolean> = new EventEmitter<boolean>();

	currentTime: string;
	duration: number;

	dragging: any = { anyDrag: false };
	showLeftObj: any = {};
	offsetObj: any = {};
	timepointOffset: any = {};
	editmode = false;
	clickCount = 0;
	keyframeSelection: KeyframeSelection;
	haveActiveKeyframe = false;
	private subscriptions: Subscription = new Subscription();

	constructor(
		private reduxService: ReduxService,
		private interactivityEditorService: InteractivityEditorService,
		private interactivityDesignDimensionsService: InteractivityDesignDimensionsService,
		private interactivityMediaService: InteractivityMediaService,
		private interactivityWidgetAnimationService: InteractivityWidgetAnimationService,
		private changeDetectorRef: ChangeDetectorRef,
		private widgetSelectionService: InteractivityWidgetSelectionService
	) { }

	ngOnInit() {
		this.subscriptions.add(this.interactivityMediaService.currentTimeSubject.subscribe(
			(time: number) => {
				if (time || time === 0) {
					if (typeof time.toFixed === 'function') {
						this.currentTime = time.toFixed(2);
					}
				}
			})
		);

		this.subscriptions.add(
			this.interactivityEditorService.durationSubject.subscribe(
				(duration: number) => {
					if (duration) {
						this.duration = duration;
						this.update();
					}
				})
		);

		this.subscriptions.add(
			this.interactivityWidgetAnimationService.keyframeSelectionSubject.subscribe((keyframeSelection: KeyframeSelection) => {
				this.keyframeSelection = keyframeSelection;
				this.haveActiveKeyframe = this.checkActiveKeyframe(keyframeSelection);
			})
		);

		this.update();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.timepointConfig && !changes.timepointConfig.firstChange) {
			this.update();
		}
	}

	checkActiveKeyframe(keyframeSelection: KeyframeSelection) {
		const a = keyframeSelection.active[this.widgetId];
		return a && this.timepointConfig && this.timepointConfig.timepoints && this.timepointConfig.timepoints.legend
			&& a[this.timepointConfig.timepoints.legend] > -1;
	}

	update() {
		if (this.timepointConfig && this.timepointConfig.timepoints && this.timepointConfig.timepoints.data) {

			this.timepointConfig.timepoints.data.sort((a: any, b: any) => parseFloat(a.time) - parseFloat(b.time)); // sort timepoints on time asc.

			for (let i = 0; i < this.timepointConfig.timepoints.data.length; i++) {
				const timepointElement = this.timepointConfig.timepoints.data[i];

				if (this.duration) {
					const offset = parseFloat(timepointElement.time) / this.duration * this.viewPortWidth;
					const offsetPercentage = parseFloat(timepointElement.time) / this.duration * 100;
					const offsetBoundary = this.viewPortWidth - 70;

					if (offsetPercentage <= 100) {
						timepointElement.timepointOffset = { left: offsetPercentage + '%' };
					} else {
						timepointElement.timepointOffset = { left: '100%' };
					}

					this.showLeftObj[i] = offset > offsetBoundary;
					if (this.dragger === 'chapter') {
						timepointElement.offset = offset;
						if (i > 0) {
							timepointElement.offsetDifference = offset - this.timepointConfig.timepoints.data[i - 1].offset;
						} else {
							timepointElement.offsetDifference = offset;
						}
					}
				}

				if (!timepointElement.id) {
					timepointElement.id = bbGenerateShortId();
				}
			}
		}
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	get viewPortWidth(): number {
		if (this.timepointContainer) {
			return +this.timepointContainer.nativeElement.offsetWidth;
		}
	}

	trackBy(i: number, item: any) {
		return item.id || i;
	}

	changeTimepointProperty(timepoint: Timepoint) {
		const value = this.interactivityDesignDimensionsService.translateValueForRedux(timepoint);
		if (value) {
			const updatedConfig = {};
			updatedConfig[timepoint.key] = value;
			const newValues: (string | number)[] = [];
			if (this.timepointConfig.pillboxConfig.length === 2) {
				this.timepointConfig.pillboxConfig.forEach((pc, i) => {
					if (pc.key === timepoint.key) {
						newValues.push(value);
					} else {
						const calculated = this.interactivityDesignDimensionsService.translateValueForRedux({ key: pc.key, val: pc.value });
						newValues.push(calculated);
					}
				});
			} else {
				newValues.push(value);
			}

			const legend = this.timepointConfig.pillboxConfig.length === 1 ? timepoint.key : this.timepointConfig.pillboxConfig.map(pc => pc.key).join(',');
			// console.warn('newValues', newValues, 'legend', legend);
			// Todo: Update selected timepoint
			this.interactivityWidgetAnimationService.addOrUpdateKeyframe(this.widgetId, newValues.join(','), legend);
		}
	}

	changeTimepointPosition(time: any, index: number) {
		if (typeof time === 'number') {
			time.toString();
		}
		if (this.dragger === 'keyframe') {
			this.dispatchKeyframeChange(time, index);
		} else {
			this.dispatchChapterChange(time, index);
		}
	}

	onAddKeyframeButtonClicked() {  // only for keyframes (for now)
		let legend: string;
		const newKeyframe: IAnimationKeyFrame = { time: this.currentTime, value: '' };
		this.haveActiveKeyframe = !this.haveActiveKeyframe;

		if (this.timepointConfig.timepoints) { // 1 or more timepoints exist
			const animation = this.timepointConfig.timepoints;
			legend = animation.legend;

			if (animation.data && animation.data.length > 0) {
				const prevValue = animation.data[animation.data.length - 1].value; // get value of last item in array
				newKeyframe.value = prevValue;
			}
		} else if (!this.timepointConfig.timepoints || (this.timepointConfig.timepoints &&
			// adding first timepoint or timepoint has no keyframes
			(!this.timepointConfig.timepoints.data || this.timepointConfig.timepoints.data.length < 1))) {
			const values = [];
			const keys = [];
			for (let i = 0, l = this.timepointConfig.pillboxConfig.length; i < l; i++) {

				const val = this.timepointConfig.pillboxConfig[i].value;
				const key = this.timepointConfig.pillboxConfig[i].key;

				const reduxValue = this.interactivityDesignDimensionsService.translateValueForRedux({ key, val });
				values.push(reduxValue);
				keys.push(key);
			}
			newKeyframe.value = values.join();

			if (!this.timepointConfig.timepoints) {
				legend = keys.join();
			} else {
				legend = this.timepointConfig.timepoints.legend;
			}
		}

		this.interactivityWidgetAnimationService.toggleKeyframe(
			this.widgetId,
			newKeyframe,
			legend
		);
	}

	onDragStart(ev: BbDragEvent, index: number) {
		ev.source.event.stopPropagation();
		this.dragging[index] = true;
		this.dragging.anyDrag = true;
		this.onDragging.emit(this.dragging.anyDrag);
	}

	onDragEnd(time: string, ev: BbDragEvent, index: number) {
		this.dragging[index] = false;
		this.dragging.anyDrag = false;
		this.onDragging.emit(this.dragging.anyDrag);

		const offset = ev.offset;

		if (offset.x === 0) {
			return;
		}

		const updatedTime = this.updateTimepoint(time, offset);

		if (this.dragger === 'keyframe') {
			this.dispatchKeyframeChange(updatedTime, index);
		} else {
			this.dispatchChapterChange(updatedTime, index);
		}
		this.changeDetectorRef.detectChanges();
	}

	updateTimepoint(copiedTime: string, offset: any): string {
		const viewPortWidth: number = this.viewPortWidth;
		const viewPortDuration: number = this.duration;
		const viewPortPixelsPerSecond = (viewPortDuration > 0) ? viewPortWidth / viewPortDuration : 1.0;

		const timepoint_num = parseFloat(copiedTime) + (offset.x / viewPortPixelsPerSecond);
		const updatedTime = (+timepoint_num.toFixed(3)).toString();

		return updatedTime;
	}

	dispatchKeyframeChange(updatedTime: string, i: number) {
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_KEYFRAME,
			id: this.widgetId,
			keyframeIndex: i,
			keyframe: { ...this.timepointConfig.timepoints.data[i], ...{ time: updatedTime } },
			legend: this.timepointConfig.timepoints.legend,
			description: 'Changed time of widget ' + this.widgetId + 'to ' + updatedTime
		});
	}

	dispatchChapterChange(updatedTime: string, i: number) {
		const chapter = this.timepointConfig.timepoints.data[i];

		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_IN_OUT_POINT,
			trackId: chapter.trackId,
			inOutPointId: chapter.id,
			inPoint: updatedTime,
			outPoint: chapter.timeOut,
			description: 'Changing time for widget: ' + chapter.widgetId
		});
	}

	changeChapterLabel(widgetId: string, newLabel: string) {
		this.interactivityEditorService.changeWidgetLabel(widgetId, newLabel);
	}

	onTimepointMousedown(timepoint: any, ev: MouseEvent) {
		ev.stopPropagation();
		let isDoubleClick = false;
		this.clickCount++;

		if (timepoint.widgetId) {
			// if item is a widget/chapter
			this.widgetSelectionService.clearSelectedWidgets();
			this.widgetSelectionService.addSelectedWidgetById(timepoint.widgetId, { area: 'tracks' });
		} else {
			// if item is a keyframe
			this.interactivityWidgetAnimationService.selectKeyframe(timepoint.id);
		}

		if (this.clickCount === 1) {
			setTimeout(() => {
				if (this.clickCount > 1) {
					isDoubleClick = true;
					this.interactivityMediaService.seekToTime(timepoint.time);
				}
				this.clickCount = 0;
				if (!isDoubleClick) {

				}
			}, 250);
		}
	}
}
